import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from './DataTable';
import { theme } from '../theme';
import { useIsMobile } from '../hooks/useMediaQuery';

const COLUMNS = [
  { key: 'id', label: 'ID' },
  { key: 'name', label: 'Nombre' },
  { key: 'description', label: 'Descripción' },
  { key: 'created_at', label: 'Creado' },
  { key: 'updated_at', label: 'Actualizado' },
];

const inputStyle = {
  width: '100%', padding: '10px 14px',
  backgroundColor: theme.colors.background,
  border: `1px solid ${theme.colors.border}`,
  borderRadius: '7px', color: theme.colors.text,
  fontSize: '0.95rem', boxSizing: 'border-box', outline: 'none',
};

const labelStyle = {
  display: 'block', color: theme.colors.textMuted,
  marginBottom: '0.4rem', fontSize: '0.85rem',
};

function Flows({ token }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const tableRef = useRef(null);
  const [successMsg, setSuccessMsg] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  const openModal = () => { setForm({ name: '', description: '' }); setFormError(''); setModalOpen(true); };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);
    try {
      const res = await fetch('/api/flows', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) { setFormError(data.message || 'Error al crear flujo'); return; }
      setModalOpen(false);
      setSuccessMsg(`Flujo "${form.name}" creado.`);
      setTimeout(() => setSuccessMsg(''), 4000);
      tableRef.current?.reload();
    } catch { setFormError('Error de conexión'); }
    finally { setFormLoading(false); }
  };

  return (
    <main style={{ padding: isMobile ? '1rem' : '2rem 1.5rem', maxWidth: '1100px', margin: '0 auto' }}>
      <div style={{ backgroundColor: theme.colors.surface, borderRadius: '14px', border: `1px solid ${theme.colors.border}`, padding: isMobile ? '1.25rem' : '2rem', boxShadow: `0 4px 24px ${theme.colors.glowSubtle}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{ color: theme.colors.primary, fontSize: '1.5rem', marginBottom: '0.3rem' }}>Flujos</h1>
            <p style={{ color: theme.colors.textMuted, fontSize: '0.9rem' }}>Flujos de conversación de WhatsApp de tu empresa.</p>
          </div>
          <button onClick={openModal} style={{ padding: '9px 20px', backgroundColor: theme.colors.primary, color: theme.colors.background, border: 'none', borderRadius: '7px', fontSize: '0.9rem', fontWeight: '700', cursor: 'pointer', width: isMobile ? '100%' : 'auto' }}>
            + Crear Flujo
          </button>
        </div>

        {successMsg && (
          <div style={{ backgroundColor: 'rgba(74,222,128,0.1)', border: `1px solid ${theme.colors.success}`, borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1.5rem', color: theme.colors.success, fontSize: '0.9rem' }}>
            {successMsg}
          </div>
        )}

        <DataTable
          ref={tableRef}
          columns={COLUMNS}
          fetchUrl="/api/flows"
          token={token}
          renderActions={(row) => (
            <button
              onClick={() => navigate(`/flows/${row.id}/edit`)}
              style={{ padding: '5px 14px', backgroundColor: theme.colors.primary, border: 'none', borderRadius: '6px', color: theme.colors.background, fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              Editar
            </button>
          )}
        />
      </div>

      {modalOpen && (
        <div onClick={() => !formLoading && setModalOpen(false)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '1rem' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: theme.colors.surface, borderRadius: '14px', border: `1px solid ${theme.colors.border}`, padding: '2rem', width: '100%', maxWidth: '400px', boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ color: theme.colors.primary, fontSize: '1.2rem' }}>Nuevo Flujo</h2>
              <button onClick={() => setModalOpen(false)} style={{ background: 'none', border: 'none', color: theme.colors.textMuted, fontSize: '1.3rem', cursor: 'pointer' }}>×</button>
            </div>
            <form onSubmit={handleCreate}>
              <div style={{ marginBottom: '1.1rem' }}>
                <label style={labelStyle}>Nombre</label>
                <input required value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} />
              </div>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={labelStyle}>Descripción <span style={{ color: theme.colors.textMuted }}>(opcional)</span></label>
                <textarea rows={3} value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
              {formError && <p style={{ color: theme.colors.error, fontSize: '0.85rem', marginBottom: '1rem', textAlign: 'center' }}>{formError}</p>}
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button type="button" onClick={() => setModalOpen(false)} disabled={formLoading} style={{ flex: 1, padding: '10px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`, borderRadius: '7px', color: theme.colors.textMuted, fontSize: '0.9rem', cursor: 'pointer' }}>Cancelar</button>
                <button type="submit" disabled={formLoading} style={{ flex: 1, padding: '10px', backgroundColor: formLoading ? theme.colors.border : theme.colors.primary, border: 'none', borderRadius: '7px', color: theme.colors.background, fontSize: '0.9rem', fontWeight: '700', cursor: formLoading ? 'not-allowed' : 'pointer' }}>
                  {formLoading ? 'Creando...' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
}

export default Flows;
